import {
  cloneDeep,
  filter,
  find,
  identity,
  map,
  uniq,
  values,
  reduce,
  flatMap,
  includes,
  uniqBy,
  orderBy,
  isNil,
} from 'lodash';
import { findSportsByIds, findAllCompetitionsByIds } from '@/services/api';
import isUuid from '@/utils/is-uuid';

export const filterTypes = {
  EVENT_ID: 'EVENT_ID',
  SPORT_AND_COMPETITION: 'SPORT_AND_COMPETITION',
  ODDS: 'ODDS',
  STAKE: 'STAKE',
  POTENTIAL_PAYOUT: 'POTENTIAL_PAYOUT',
  BET_PARTS: 'BET_PARTS',
  CLIENT: 'CLIENT',
  STAKE_FACTOR: 'STAKE_FACTOR',
  MAX_BET_PERCENTAGE: 'MAX_BET_PERCENTAGE',
  EVENTS: 'EVENTS',
};

export const dataTypes = {
  NUMBER: 'NUMBER',
  PERCENTAGE: 'PERCENTAGE',
  UUID: 'UUID',
  SPORT_AND_COMPETITION: 'SPORT_AND_COMPETITION',
  CLIENT: 'CLIENT',
  EVENTS: 'EVENTS',
};

export const conditionTypes = {
  LESS_THAN: 'LESS_THAN',
  LESS_OR_EQUAL: 'LESS_OR_EQUAL',
  EQUAL: 'EQUAL',
  GREATER_OR_EQUAL: 'GREATER_OR_EQUAL',
  GREATER_THAN: 'GREATER_THAN',
};

const filterDefs = {
  [filterTypes.EVENT_ID]: {
    id: filterTypes.EVENT_ID,
    type: dataTypes.UUID,
    label: 'Event ID',
    keys: ['eventId'],
  },
  [filterTypes.SPORT_AND_COMPETITION]: {
    id: filterTypes.SPORT_AND_COMPETITION,
    type: dataTypes.SPORT_AND_COMPETITION,
    label: 'Sport & Competition',
    keys: ['sport', 'competition', 'sportMultiSelect', 'competitionMultiSelect'],
    hideLabel: true,
    conditionType: conditionTypes.EQUAL,
  },
  [filterTypes.ODDS]: {
    id: filterTypes.ODDS,
    type: dataTypes.NUMBER,
    label: 'Odds',
    keys: ['odds'],
  },
  [filterTypes.STAKE]: {
    id: filterTypes.STAKE,
    type: dataTypes.PERCENTAGE,
    label: 'Stake',
    keys: ['stake'],
  },
  [filterTypes.POTENTIAL_PAYOUT]: {
    id: filterTypes.POTENTIAL_PAYOUT,
    type: dataTypes.PERCENTAGE,
    label: 'Potential Payout',
    keys: ['potentialPayout'],
  },
  [filterTypes.STAKE_FACTOR]: {
    id: filterTypes.STAKE_FACTOR,
    type: dataTypes.PERCENTAGE,
    label: 'Stake factor',
    keys: ['stakeFactor'],
  },
  [filterTypes.MAX_BET_PERCENTAGE]: {
    id: filterTypes.MAX_BET_PERCENTAGE,
    type: dataTypes.PERCENTAGE,
    label: '% Max Bet',
    keys: ['maxBetPercentage'],
  },
  [filterTypes.BET_PARTS]: {
    id: filterTypes.BET_PARTS,
    type: dataTypes.NUMBER,
    label: 'Bet Parts',
    keys: ['betParts'],
  },
  [filterTypes.CLIENT]: {
    id: filterTypes.CLIENT,
    type: dataTypes.CLIENT,
    label: 'Client',
    keys: ['client'],
    hideLabel: true,
    parameter: {
      clients: [],
    },
    conditionType: conditionTypes.EQUAL,
  },
  [filterTypes.EVENTS]: {
    id: filterTypes.EVENTS,
    type: dataTypes.EVENTS,
    label: 'Events',
    keys: ['events'],
    parameter: {
      events: [],
    },
    conditionType: conditionTypes.EQUAL,
  },
};

export const getFilterDef = (id) => {
  const foundFilterDef = filterDefs[id];
  if (!foundFilterDef) return null;
  return cloneDeep(foundFilterDef);
};

export const getFilterOptions = (isAdmin) => {
  let filters = cloneDeep(values(filterDefs));
  if (!isAdmin) {
    filters = filter(filters, (item) => item.id !== filterTypes.CLIENT);
  }
  // Added in order not to remove the event filter definition from config
  filters = filter(filters, (item) => item.id !== filterTypes.EVENT_ID);

  return orderBy(filters, 'label');
};

const filterValidators = {
  [filterTypes.EVENT_ID](parameter) {
    return (!parameter?.length || isUuid(parameter)) ? '' : 'Event ID must be a valid UUID.';
  },
};

export const getFilterError = (filterItem) => {
  const validator = filterValidators[filterItem.id];
  if (!validator) return '';
  return validator(filterItem.parameter);
};

const conditionTypeDefs = {
  [conditionTypes.EQUAL]: {
    id: conditionTypes.EQUAL,
    label: 'Equals (=)',
    shortLabel: 'Equals',
  },
  [conditionTypes.LESS_THAN]: {
    id: conditionTypes.LESS_THAN,
    label: 'Less than (<)',
    shortLabel: 'Less than',
  },
  [conditionTypes.LESS_OR_EQUAL]: {
    id: conditionTypes.LESS_OR_EQUAL,
    label: 'Less than or equal to (<=)',
    shortLabel: 'Less than or equal to',
  },
  [conditionTypes.GREATER_THAN]: {
    id: conditionTypes.GREATER_THAN,
    label: 'Greater than (>)',
    shortLabel: 'Greater than',
  },
  [conditionTypes.GREATER_OR_EQUAL]: {
    id: conditionTypes.GREATER_OR_EQUAL,
    label: 'Greater than or equal to (>=)',
    shortLabel: 'Greater than or equal to',
  },
};

export const getConditionTypeOptions = () => cloneDeep(values(conditionTypeDefs));

const filterParsers = {
  // UUId filter is not used in the current implementation
  // [filterTypes.EVENT_ID](allFilters) {
  //   const eventIdFilter = find(allFilters, { type: 'eventId' });

  //   const definition = filterDefs[filterTypes.EVENT_ID];
  //   const conditionType = conditionTypeDefs[eventIdFilter?.conditionType] || null;
  //   const parameter = eventIdFilter?.parameter || null;

  //   return {
  //     ...definition,
  //     conditionType,
  //     parameter,
  //   };
  // },
  async [filterTypes.SPORT_AND_COMPETITION](allFilters) {
    // 'sport' type is for backward compatibility
    // and should be removed in the future
    const sportFilter = find(allFilters, { type: 'sport' });
    const sportsFilter = find(allFilters, { type: 'sportMultiSelect' });
    const sportsFilterIds = [
      ...(sportFilter ? [sportFilter.parameter] : []),
      ...(sportsFilter ? sportsFilter.parameter : []),
    ];
    const sportsFilterObjects = await findSportsByIds(sportsFilterIds);
    // 'competition' type is for backward compatibility
    // and should be removed in the future
    const competitionFilter = flatMap(
      filter(allFilters, { type: 'competition' }),
      (filterItem) => filterItem.parameter,
    );
    const competitionsFilter = find(allFilters, { type: 'competitionMultiSelect' });
    const competitionFilterIds = [
      ...(competitionFilter || []),
      ...(competitionsFilter ? competitionsFilter.parameter : []),
    ];
    const competitionFilterObjects = await findAllCompetitionsByIds(competitionFilterIds);

    const definition = filterDefs[filterTypes.SPORT_AND_COMPETITION];
    const conditionType = conditionTypeDefs[conditionTypes.EQUAL];
    const parameter = {
      sports: sportsFilterObjects,
      competitions: competitionFilterObjects,
    };

    return {
      ...definition,
      conditionType,
      parameter,
    };
  },
  [filterTypes.ODDS](allFilters, { selectedOddFormat }) {
    const oddsFilter = find(allFilters, { type: 'odds' });

    let odd = oddsFilter.parameter?.decimalPrice || 0;
    if (selectedOddFormat === 'american') {
      const originalPrice = oddsFilter.parameter?.originalFormattedPrice || {};
      odd = originalPrice?.signIsPlus ? originalPrice?.value || 0 : -Math.abs(originalPrice?.value || 0);
    }
    if (selectedOddFormat === 'probability') {
      odd = oddsFilter.parameter?.probability || 0;
    }

    return {
      ...filterDefs[filterTypes.ODDS],
      conditionType: conditionTypeDefs[oddsFilter.conditionType],
      parameter: odd,
    };
  },
  [filterTypes.STAKE](allFilters) {
    const stakeFilter = find(allFilters, { type: 'stake' });
    return {
      ...filterDefs[filterTypes.STAKE],
      conditionType: conditionTypeDefs[stakeFilter.conditionType] || null,
      parameter: isNil(stakeFilter.parameter) ? 0 : stakeFilter.parameter / 100,
    };
  },
  [filterTypes.POTENTIAL_PAYOUT](allFilters) {
    const potentialPayoutFilter = find(allFilters, { type: 'potentialPayout' });
    return {
      ...filterDefs[filterTypes.POTENTIAL_PAYOUT],
      conditionType: conditionTypeDefs[potentialPayoutFilter.conditionType] || null,
      parameter: isNil(potentialPayoutFilter.parameter) ? 0 : potentialPayoutFilter.parameter / 100,
    };
  },
  [filterTypes.STAKE_FACTOR](allFilters) {
    const stakeFactorFilter = find(allFilters, { type: 'stakeFactor' });
    return {
      ...filterDefs[filterTypes.STAKE_FACTOR],
      conditionType: conditionTypeDefs[stakeFactorFilter.conditionType] || null,
      parameter: isNil(stakeFactorFilter.parameter) ? 0 : stakeFactorFilter.parameter * 100,
    };
  },
  [filterTypes.MAX_BET_PERCENTAGE](allFilters) {
    const maxBetPercentageFilter = find(allFilters, { type: 'maxBetPercentage' });
    return {
      ...filterDefs[filterTypes.MAX_BET_PERCENTAGE],
      conditionType: conditionTypeDefs[maxBetPercentageFilter.conditionType] || null,
      parameter: isNil(maxBetPercentageFilter.parameter) ? 0 : maxBetPercentageFilter.parameter * 100,
    };
  },
  [filterTypes.BET_PARTS](allFilters) {
    const betPartsFilter = find(allFilters, { type: 'betParts' });
    return {
      ...filterDefs[filterTypes.BET_PARTS],
      conditionType: conditionTypeDefs[betPartsFilter.conditionType] || null,
      parameter: isNil(betPartsFilter.parameter) ? 0 : betPartsFilter.parameter,
    };
  },
  [filterTypes.CLIENT](allFilters) {
    const clientFilters = filter(allFilters, { type: 'client' });

    const definition = filterDefs[filterTypes.CLIENT];
    const conditionType = conditionTypeDefs[conditionTypes.EQUAL];

    const validClientFilters = filter(clientFilters, (clientFilter) => clientFilter.parameter !== undefined);

    const clients = map(validClientFilters, (clientFilter) => ({
      operatorId: clientFilter.parameter,
    }));

    const parameter = {
      clients,
    };

    return {
      ...definition,
      conditionType,
      parameter,
    };
  },
  [filterTypes.EVENT_ID](allFilters) {
    const eventFilters = filter(allFilters, { type: 'eventId' });

    const definition = filterDefs[filterTypes.EVENTS];
    const conditionType = conditionTypeDefs[conditionTypes.EQUAL];

    const validClientFilters = filter(eventFilters, (eventFilter) => eventFilter.parameter !== undefined);

    const eventIds = map(validClientFilters, (eventFilter) => eventFilter.parameter);

    const parameter = {
      events: eventIds,
    };

    return {
      ...definition,
      conditionType,
      parameter,
    };
  },
};

export const parseFilter = async (filterDef, allFilters, context = {}) => {
  const parser = filterParsers[filterDef.id];

  if (!parser) {
    console.warn('Parsing unsupported filter type:', filterDef.id);
    return null;
  }

  return parser(allFilters, context);
};

export const parseAllFilters = async (allFilters, context = {}) => {
  const allFilterTypes = uniq(map(allFilters, 'type'));
  const mappedFilterDefs = uniqBy(reduce(allFilterTypes, (result, filterType) => {
    const foundFilterDef = find(filterDefs, (def) => includes(def.keys, filterType));
    if (!foundFilterDef) return result;
    return [...result, foundFilterDef];
  }, []), 'id');
  const parsedFiltersRequests = map(mappedFilterDefs, (filterDef) => parseFilter(filterDef, allFilters, context));
  const parsedFilters = await Promise.all(parsedFiltersRequests);
  return filter(parsedFilters, identity);
};

const filterComposers = {
  [filterTypes.EVENT_ID](parsedFilter) {
    const parameter = parsedFilter.parameter || '';

    return [{
      type: 'eventId',
      conditionType: conditionTypes.EQUAL,
      parameter,
    }];
  },
  [filterTypes.SPORT_AND_COMPETITION](parsedFilter) {
    if (!Array.isArray(parsedFilter.parameter?.sports) && !parsedFilter.parameter?.sports.length) return [];
    const sportFilter = {
      type: 'sportMultiSelect',
      conditionType: conditionTypes.EQUAL,
      parameter: map(parsedFilter.parameter?.sports, (sport) => sport.sportId) || [],
    };
    const competitionFilter = {
      type: 'competitionMultiSelect',
      conditionType: conditionTypes.EQUAL,
      parameter: map(parsedFilter.parameter?.competitions, (competition) => competition.competitionId) || [],
    };

    return [
      sportFilter,
      competitionFilter,
    ];
  },
  [filterTypes.ODDS](parsedFilter, { selectedOddFormat }) {
    let formattedParameter = {};
    if (selectedOddFormat === 'american') {
      formattedParameter = {
        originalFormattedPrice: {
          signIsPlus: parsedFilter.parameter >= 0,
          value: Math.abs(parsedFilter.parameter),
          priceType: 'american',
        },
        format: 'ORIGINAL_FORMATTED_PRICE',
      };
    }
    if (selectedOddFormat === 'decimal') {
      formattedParameter = {
        decimalPrice: parsedFilter.parameter,
        format: 'DECIMAL_PRICE',
      };
    }
    if (selectedOddFormat === 'probability') {
      formattedParameter = {
        probability: parseFloat(parsedFilter.parameter / 100),
        format: 'PROBABILITY',
      };
    }

    return {
      type: 'odds',
      conditionType: parsedFilter.conditionType?.id || '',
      parameter: formattedParameter,
    };
  },
  [filterTypes.STAKE](parsedFilter) {
    return {
      type: 'stake',
      conditionType: parsedFilter.conditionType?.id || '',
      parameter: isNil(parsedFilter.parameter) ? 0 : parsedFilter.parameter * 100,
    };
  },
  [filterTypes.POTENTIAL_PAYOUT](parsedFilter) {
    return {
      type: 'potentialPayout',
      conditionType: parsedFilter.conditionType?.id,
      parameter: isNil(parsedFilter.parameter) ? 0 : parsedFilter.parameter * 100,
    };
  },
  [filterTypes.STAKE_FACTOR](parsedFilter) {
    return {
      type: 'stakeFactor',
      conditionType: parsedFilter.conditionType?.id,
      parameter: isNil(parsedFilter.parameter) ? 0 : parsedFilter.parameter / 100,
    };
  },
  [filterTypes.MAX_BET_PERCENTAGE](parsedFilter) {
    return {
      type: 'maxBetPercentage',
      conditionType: parsedFilter.conditionType?.id,
      parameter: isNil(parsedFilter.parameter) ? 0 : parsedFilter.parameter / 100,
    };
  },
  [filterTypes.BET_PARTS](parsedFilter) {
    return {
      type: 'betParts',
      conditionType: parsedFilter.conditionType?.id,
      parameter: isNil(parsedFilter.parameter) ? 0 : parsedFilter.parameter,
    };
  },
  [filterTypes.CLIENT](parsedFilter) {
    const clientFilters = map(parsedFilter.parameter?.clients || [], ({ operatorId }) => ({
      type: 'client',
      conditionType: conditionTypes.EQUAL,
      parameter: operatorId,
    }));

    return [
      ...clientFilters,
    ];
  },
  [filterTypes.EVENTS](parsedFilter) {
    const eventFilters = map(parsedFilter.parameter?.events || [], (eventId) => ({
      type: 'eventId',
      conditionType: conditionTypes.EQUAL,
      parameter: eventId,
    }));

    return [
      ...eventFilters,
    ];
  },
};

export const composeFilter = (parsedFilter, context = {}) => {
  const parser = filterComposers[parsedFilter.id];

  if (!parser) {
    console.warn('Composing unsupported filter type:', parsedFilter.id);
    return null;
  }

  return parser(parsedFilter, context);
};

export const composeAllFilters = (allParsedFilters, context = {}) => flatMap(allParsedFilters, (filterItem) => composeFilter(filterItem, context));
